import type {SafeHTMLString, SanitizedHTML} from '@github-ui/safe-html'
import {type Config, createPurifier, sanitizeWithPurifier} from '@github-ui/sanitize'

// DOM PURIFY CONFIGURATION
// docs: https://github.com/cure53/DOMPurify

// Allowed class names that can appear in the 'class' attribute
// Based on security review, we restrict class values to only these names
// to prevent potential XSS attacks through CSS class manipulation
// (e.g., JavaScript-targeting class names like js-comment-edit-button)
// We allow 'emoji' for custom GitHub emojis and 'g-emoji' for the g-emoji component
const ALLOWED_CLASS_NAMES = new Set(['emoji', 'g-emoji'])

// Tags that are allowed to have the 'class' attribute
// Since we only allow the 'emoji' and 'g-emoji' class names, we restrict this to just
// the tags that need them for GitHub custom emoji rendering
const CLASS_ATTRIBUTE_ALLOWLIST = new Set([
  'g-emoji',
  'img', // Required for custom GitHub emojis (class="emoji")
])

// Use this config to add "global" sanitization rules
// to every other sanitization config object
const SHARED_SANITIZATION_CONFIG: Readonly<Config> = {
  FORBID_TAGS: ['style'],
  ALLOW_DATA_ATTR: false,
}

const TEXT_SANITIZATION_CONFIG: Readonly<Config> = {
  ...SHARED_SANITIZATION_CONFIG,

  // Using ALLOWED_TAGS instead of ADD_TAGS overrides the
  // default DOMPurify config, making only the tags explicitly
  // allowed by this config to be considered safe
  ALLOWED_TAGS: [
    // a: allowed in memex text custom fields
    'a',
    '#text',
    // img and g-emoji to support github emojis
    'img',
    'g-emoji',
    // to allow code inside issue/pr titles
    'code',
  ],
  ADD_ATTR: [
    // attributes for g-emoji component
    'alias',
    'fallback-src',
  ],
  // Note: 'class' is not in FORBID_ATTR because we need the uponSanitizeAttribute
  // hook to run first to selectively allow it on all tags in CLASS_ATTRIBUTE_ALLOWLIST
  FORBID_ATTR: ['style', 'hidden', 'id'],
  KEEP_CONTENT: true,
}

// This config uses DOMPurify default allowed tags/attrs and adds
// the custom tags and attrs used in the rendered result of GitHub Flavored Markdown
// - default allowed tags: https://github.com/cure53/DOMPurify/blob/main/src/tags.js
// - default allowed attrs: https://github.com/cure53/DOMPurify/blob/main/src/attrs.js
const RENDERED_MARKDOWN_SANITIZATION_CONFIG: Readonly<Config> = {
  ...SHARED_SANITIZATION_CONFIG,

  ADD_TAGS: [
    'action-menu',
    'anchored-position',
    'auto-complete',
    'focus-group',
    'g-emoji',
    'tracking-block',
    'tracking-block-omnibar',
    'tasklist-block-title',
    'details-menu',
    // GH math expressions
    // https://docs.github.com/get-started/writing-on-github/working-with-advanced-formatting/writing-mathematical-expressions
    'math-renderer',
    // classic task lists support
    'task-lists',
    // used by unfurled labels
    // https://docs.github.com/get-started/writing-on-github/working-with-advanced-formatting/autolinked-references-and-urls#labels
    'tool-tip',
    'turbo-frame',
    'modal-dialog',
    // Used by tasklist-block-title
    'primer-text-field',
  ],
  ADD_ATTR: [
    // attributes for g-emoji components
    'alias',
    'anchor',
    'anchor-offset',
    'fallback-src',
    // attributes for hovercard attributes attached to anchor tags
    'data-hovercard-type',
    'data-hovercard-url',
    'data-octo-click',
    'data-octo-dimensions',
    // attributes for mermaidjs rich rendering
    'data-identity',
    'data-host',
    'data-src',
    'data-content',
    'data-type',
    'data-json',
    'data-plain',
    // attributes for tracking-block
    'data-id',
    'data-issue',
    'data-draft-issue',
    'data-item-uuid',
    'data-item-id',
    'data-item-title',
    'data-item-state',
    'data-item-position',
    'data-menu-type',
    'data-repository-id',
    'data-repository-name',
    'data-show-dialog-id',
    'data-close-dialog-id',
    'data-modal-dialog-overlay',
    'data-display-number',
    'data-hydro-click-hmac',
    'data-hydro-click-payload',
    'data-hydro-click',
    'data-tracking-block-item-title',
    'data-tracking-block-convert-button',
    'data-tracking-block-remove-button',
    'data-tracking-block-draft-title',
    'data-form-tracking-block-update',
    'data-completion-completed',
    'data-completion-total',
    'data-response-source-type',
    'data-query-type',
    'data-precache',
    // attributes for task-lists
    'sortable',
    // attributes for catalyst
    'data-view-component',
    'data-catalyst',
    'data-action',
    'data-target',
    'data-targets',
    // attributes for enriched video element rendered in issue body or comments
    'data-canonical-src',
    // attributes for mathjax
    'mnemonics',
    'popover',
    'popovertarget',
    'retain',
    'side',
  ],
  // NOTE: even though the data-action doesn't use URIs, the fact that it possibly
  //       includes a colon causes the dompurify to detect it as a URI and remove it if it's not
  //       add as a URI SAFE ATTR. (more info: https://github.github.io/catalyst/guide/actions/)
  // NOTE: data-item-title is used by the tracking block component and the attribute was being
  //       sanitized out if the title included a colon
  ADD_URI_SAFE_ATTR: ['data-action', 'data-item-title', 'data-plain', 'data-host', 'data-content', 'data-json'],
}

/** Domains which are controlled by GitHub and considered safe to allow linking to. */
const GITHUB_CONTROLLED_DOMAINS = new Set([
  'github.com',
  'githubusercontent.com',
  'githubassets.com',
  'github.localhost',
  'ghe.com',
  'ghe.localhost',
])

/** Subdomains that should be allowed when subdomain isolation is enabled in GHES */
const GHES_SUBDOMAINS = new Set(['media', 'uploads', 'assets', 'avatars'])

// recommended approach for ensuring all sanitized links open in a new window
// https://github.com/cure53/DOMPurify/tree/2ac080246d7ee977cb820abfa1990a683692ce09/demos#hook-to-open-all-links-in-a-new-window-link
const sanitizeNewWindowLinks = (node: Element) => {
  // set all elements owning target to target=_blank
  if ('target' in node || node instanceof HTMLAnchorElement) {
    node.setAttribute('target', '_blank')
    // prevent https://www.owasp.org/index.php/Reverse_Tabnabbing
    node.setAttribute('rel', 'noopener noreferrer')
  }
  // set non-HTML/MathML links to xlink:show=new
  // eslint-disable-next-line github/get-attribute
  if (!node.hasAttribute('target') && (node.hasAttribute('xlink:href') || node.hasAttribute('href'))) {
    // eslint-disable-next-line github/get-attribute
    node.setAttribute('xlink:show', 'new')
  }
}

// Only allow images from github-controlled domains to be linked from images. The intention here is to allow for our
// custom images like :octocat: to be shown, not to allow any user-controlled image to be shown. Note that this is not
// really a security concern, regardless of the URI, as the content is still filtered with DOMPurify. We are just removing
// unexpected hostnames for the sake of being explicit in what we support.
const sanitizeNonGitHubImageLinks = (node: Node) => {
  if (node instanceof Element && node.tagName === 'IMG') {
    const imageSrc = node.getAttribute('src')
    if (imageSrc) {
      try {
        const url = new URL(imageSrc, document.baseURI)
        const urlParts = url.hostname.split('.')
        if (urlParts.length >= 2) {
          const baseDomain = `${urlParts.at(-2)}.${urlParts.at(-1)}`
          // Allow github-controlled domains explicitly, and nothing else
          // Based on the set of supported URIs in our Content Security Policy
          if (GITHUB_CONTROLLED_DOMAINS.has(baseDomain)) return
          // Check for relative URL
          const isRelativeURL = new URL(document.baseURI).origin === new URL(imageSrc, document.baseURI).origin
          if (isRelativeURL) return
          // Also allow the current GitHub URL
          const githubUrlScript = document.getElementById('github-url')
          if (githubUrlScript) {
            const githubUrl = new URL(githubUrlScript.textContent ?? '')
            const validSubdomains = Array.from(GHES_SUBDOMAINS.values()).map(
              subdomain => `${subdomain}.${githubUrl.hostname}`,
            )
            if (validSubdomains.includes(url.hostname)) return
          }
        }
        node.parentNode?.removeChild(node)
      } catch {
        // If the URL fails to parse for some reason, we just remove the element
        node.parentNode?.removeChild(node)
      }
    }
  }
}

/**
 * Hook to selectively allow 'class' attribute only on allowlisted tags,
 * and filter class values to only allowed class names.
 *
 * This hook is used with DOMPurify's 'uponSanitizeAttribute' event to implement
 * tag-specific attribute filtering. It prevents potential XSS attacks through CSS
 * class manipulation by:
 * 1. Restricting the class attribute to only structural/content HTML tags defined
 *    in CLASS_ATTRIBUTE_ALLOWLIST
 * 2. Filtering class values to only those in ALLOWED_CLASS_NAMES (e.g., "emoji")
 *
 * @param node - The DOM element being sanitized
 * @param data - Sanitization data containing attrName, attrValue, and keepAttr flag
 * @see https://github.com/github/memex/issues/24146
 */
const sanitizeClassAttribute = (node: Element, data: {attrName: string; attrValue: string; keepAttr: boolean}) => {
  if (data.attrName === 'class') {
    const tagName = node.tagName.toLowerCase()
    if (!CLASS_ATTRIBUTE_ALLOWLIST.has(tagName)) {
      data.keepAttr = false
      return
    }

    // Filter class values to only allowed class names
    const classNames = data.attrValue.split(/\s+/).filter(className => ALLOWED_CLASS_NAMES.has(className))
    if (classNames.length === 0) {
      data.keepAttr = false
    } else {
      data.attrValue = classNames.join(' ')
    }
  }
}

const purifier = createPurifier(instance => {
  instance.addHook('afterSanitizeAttributes', sanitizeNewWindowLinks)
  instance.addHook('afterSanitizeElements', sanitizeNonGitHubImageLinks)
  instance.addHook('uponSanitizeAttribute', sanitizeClassAttribute)
})

// Purifier that does not perform image link sanitization
const nonImagePurifier = createPurifier(instance => {
  instance.addHook('afterSanitizeAttributes', sanitizeNewWindowLinks)
  instance.addHook('uponSanitizeAttribute', sanitizeClassAttribute)
})

/**
 * Sanitize untrusted text-input HTML for injection at a DOM sink.
 *
 * Returns the full {@link SanitizedHTML} union so the sanitized value flows unbroken to
 * `dangerouslySetInnerHTML` — minting a real `TrustedHTML` when Trusted Types are
 * enforced. Use this at the render sink (`SanitizedHtml`); for option-model fields typed
 * `SafeHTMLString`, use {@link sanitizeTextInputHtmlString} instead.
 */
export function sanitizeTextInputHtml(html: string): SanitizedHTML {
  return sanitizeWithPurifier(html, purifier, TEXT_SANITIZATION_CONFIG)
}

/**
 * Sanitize untrusted text-input HTML for memex option-model fields.
 *
 * Returns a plain {@link SafeHTMLString} (never a minted `TrustedHTML`) because these
 * values are stored on `SafeHTMLString`-typed model fields rather than injected into the
 * DOM here. Passing `RETURN_TRUSTED_TYPE: false` avoids minting a policy value we'd only
 * discard. The render sink (`SanitizedHtml`) re-sanitizes via {@link sanitizeTextInputHtml},
 * which mints when Trusted Types are enforced.
 */
export function sanitizeTextInputHtmlString(html: string): SafeHTMLString {
  return sanitizeWithPurifier(html, purifier, {...TEXT_SANITIZATION_CONFIG, RETURN_TRUSTED_TYPE: false})
}

/**
 * Collapse a {@link SanitizedHTML} union back to the {@link SafeHTMLString} brand for
 * memex option-model fields (e.g. `PersistedOption.nameHtml`) when the value comes from
 * a package whose field is typed as the wider `SanitizedHTML` (e.g.
 * `PersistedSingleSelectOption.nameHtml` from shared-single-select-components).
 *
 * SECURITY: the input is already sanitized; this only re-brands an already-safe value — it
 * does not accept arbitrary strings. This is a transitional compatibility bridge, not a
 * general-purpose conversion utility. memex is outside the Trusted Types enforced subset, so
 * its option-model fields carry branded strings rather than real `TrustedHTML`, and the one
 * render sink (`SanitizedHtml`) re-sanitizes its input independently.
 *
 * DELETE when memex joins the enforced subset and these fields carry sanitized HTML
 * unbroken to a DOM sink (then widen the fields to `SanitizedHTML` instead).
 */
export function narrowSanitizedToSafeHtml(html: SanitizedHTML): SafeHTMLString {
  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- re-branding an already-sanitized value; see doc comment above.
  return html as unknown as SafeHTMLString
}

export function sanitizeRenderedMarkdown(
  html: string,
  {skipImageSanitization = false}: {skipImageSanitization?: boolean} = {},
) {
  if (skipImageSanitization) {
    return sanitizeWithPurifier(html, nonImagePurifier, RENDERED_MARKDOWN_SANITIZATION_CONFIG)
  }

  return sanitizeWithPurifier(html, purifier, RENDERED_MARKDOWN_SANITIZATION_CONFIG)
}
